import type { ListBoxItemProps as AriaListBoxItemProps } from 'react-aria-components';

import { Check } from '@untitledui/icons/Check';
import { isValidElement, useContext } from 'react';
import {
  ListBoxItem as AriaListBoxItem,
  Text as AriaText,
} from 'react-aria-components';

import { Avatar } from '@/components/base/avatar/avatar';
import { CheckboxBase } from '@/components/base/checkbox/checkbox';
import { RadioButtonBase } from '@/components/base/radio-buttons/radio-buttons';
import { cx } from '@/utils/cx';
import { isReactComponent } from '@/utils/is-react-component';

import type { SelectItemType } from './select';

import { SelectContext } from './select';

const sizes = {
  xs: 'p-1.5 pr-2',
  sm: 'p-2 pr-2.5',
  md: 'p-2.5 pl-2',
};

// Symmetric padding used when a checkbox affordance replaces the trailing
// "selected" check, so the row doesn't end up lopsided.
const checkboxSizes = {
  xs: 'p-1.5',
  sm: 'px-2.5 py-2',
  md: 'px-2.5 py-2',
};

interface SelectItemProps
  extends Omit<AriaListBoxItemProps<SelectItemType>, 'id'>, SelectItemType {
  /**
   * Render a leading checkbox that reflects `state.isSelected` and hide the
   * trailing check icon. Use for multi-select listbox rows where each option
   * should look like a checkable choice rather than a single-select highlight.
   */
  showCheckbox?: boolean;
  /**
   * Like `showCheckbox`, but with a leading radio button. Use for
   * single-select listbox rows where exactly one option is always active.
   */
  showRadio?: boolean;
}

export const SelectItem = ({
  label,
  id,
  value,
  avatarUrl,
  supportingText,
  isDisabled,
  icon: Icon,
  className,
  children,
  showCheckbox = false,
  showRadio = false,
  ...props
}: SelectItemProps) => {
  const { size } = useContext(SelectContext);

  // A leading checkbox/radio takes over the selection visuals: the row stays
  // neutral and the trailing check icon is dropped.
  const hasLeadingControl = showCheckbox || showRadio;

  const labelOrChildren =
    label || (typeof children === 'string' ? children : '');
  const textValue = supportingText
    ? labelOrChildren + ' ' + supportingText
    : labelOrChildren;

  return (
    <AriaListBoxItem
      id={id}
      value={
        value ?? {
          id,
          label: labelOrChildren,
          avatarUrl,
          supportingText,
          isDisabled,
          icon: Icon,
        }
      }
      textValue={textValue}
      isDisabled={isDisabled}
      {...props}
      className={(state) =>
        cx(
          'w-full px-1.5 py-px outline-hidden',
          typeof className === 'function' ? className(state) : className,
        )
      }
    >
      {(state) => (
        <div
          className={cx(
            'flex cursor-pointer items-center gap-2 rounded-md outline-hidden select-none',
            state.isSelected && !hasLeadingControl && 'bg-active',
            state.isDisabled && 'cursor-not-allowed',
            state.isFocused && 'bg-primary_hover',
            state.isFocusVisible && 'ring-2 ring-focus-ring ring-inset',

            // Icon styles
            size === 'xs'
              ? '*:data-icon:size-4 *:data-icon:shrink-0 *:data-icon:text-fg-quaternary'
              : '*:data-icon:size-5 *:data-icon:shrink-0 *:data-icon:text-fg-quaternary',
            state.isDisabled && '*:data-icon:text-fg-disabled',

            hasLeadingControl ? checkboxSizes[size] : sizes[size],
          )}
        >
          {showCheckbox ? (
            <CheckboxBase
              size="sm"
              isSelected={state.isSelected}
              isDisabled={state.isDisabled}
              isFocusVisible={state.isFocusVisible}
            />
          ) : showRadio ? (
            <RadioButtonBase
              size="sm"
              isSelected={state.isSelected}
              isDisabled={state.isDisabled}
              isFocusVisible={state.isFocusVisible}
            />
          ) : avatarUrl ? (
            <Avatar aria-hidden="true" size="xs" src={avatarUrl} alt={label} />
          ) : isReactComponent(Icon) ? (
            <Icon data-icon aria-hidden="true" />
          ) : isValidElement(Icon) ? (
            Icon
          ) : null}

          <div className="flex w-full min-w-0 flex-1 flex-wrap gap-x-2">
            <AriaText
              slot="label"
              className={cx(
                'truncate font-medium whitespace-nowrap text-primary',
                size === 'xs'
                  ? 'text-xs'
                  : size === 'sm'
                    ? 'text-sm'
                    : 'text-md',
                state.isDisabled && 'text-disabled',
              )}
            >
              {label ||
                (typeof children === 'function' ? children(state) : children)}
            </AriaText>

            {supportingText && (
              <AriaText
                slot="description"
                className={cx(
                  'whitespace-nowrap text-tertiary',
                  size === 'xs'
                    ? 'text-xs'
                    : size === 'sm'
                      ? 'text-sm'
                      : 'text-md',
                  state.isDisabled && 'text-disabled',
                )}
              >
                {supportingText}
              </AriaText>
            )}
          </div>

          {state.isSelected && !hasLeadingControl && (
            <Check
              aria-hidden="true"
              className={cx(
                'ml-auto text-fg-brand-primary',
                size === 'xs'
                  ? 'size-3.5 stroke-[2.5px]'
                  : size === 'sm'
                    ? 'size-4 stroke-[2.5px]'
                    : 'size-5',
                state.isDisabled && 'text-fg-disabled',
              )}
            />
          )}
        </div>
      )}
    </AriaListBoxItem>
  );
};
